import { authFetch } from './auth-fetch';
import type { Page } from './endpoints';

/**
 * The twelve calls of `12 §11` — the agreement as both managers drive it.
 *
 * Same rule as every module here: the wire shape verbatim, no client-side
 * modelling. `scope` in particular travels as the raw document of `07 §2.1`,
 * because the decision screen's whole job is to show exactly what would be
 * agreed, not a summary of it.
 */

export type AgreementStatus =
  | 'draft'
  | 'proposed'
  | 'accepted'
  | 'rejected'
  | 'suspended'
  | 'expired'
  | 'revoked';

export interface AgreementParty {
  id: string;
  name: string;
  slug: string;
}

export interface SharedRepository {
  company_slug: string;
  repository_slug: string;
  access: 'read' | 'write';
}

export interface AgreementScope {
  allowed_topics?: string[];
  allow_task_delegation?: boolean;
  allow_repository_sharing?: boolean;
  shared_repositories?: SharedRepository[];
  allow_file_transfer?: boolean;
  allow_knowledge_sharing?: boolean;
  message_limits?: { max_per_day?: number; max_body_chars?: number };
  requires_approval_for?: string[];
  confidentiality_note?: string;
}

export interface AgreementTeam {
  company_id: string;
  team_id: string;
  team_name: string;
  team_slug: string;
}

export interface Agreement {
  id: string;
  title: string;
  status: AgreementStatus;
  initiator: AgreementParty;
  counterpart: AgreementParty;
  scope: AgreementScope;
  terms: string | null;
  valid_from: string | null;
  valid_until: string | null;
  proposed_by_user_id: string;
  decided_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgreementDetail extends Agreement {
  teams: AgreementTeam[];
}

export interface AgreementActivity {
  messages_last_7_days: number;
  messages_by_day: { date: string; count: number }[];
  top_topics: { topic: string; count: number }[];
  delegations: number;
  blocked_attempts: number;
}

export interface DirectoryCompany {
  id: string;
  name: string;
  slug: string;
}

export interface AgreementDraftBody {
  counterpart_company_id?: string;
  title: string;
  terms: string | null;
  scope: AgreementScope;
  team_ids: string[];
  valid_from: string | null;
  valid_until: string | null;
}

export const agreements = {
  list: (companyId: string, status?: AgreementStatus | '') =>
    authFetch<Page<Agreement>>(
      `/companies/${companyId}/agreements${status ? `?filter[status]=${status}` : ''}`,
    ),
  create: (companyId: string, body: AgreementDraftBody) =>
    authFetch<AgreementDetail>(`/companies/${companyId}/agreements`, { method: 'POST', body }),
  show: (id: string) => authFetch<AgreementDetail>(`/agreements/${id}`),
  update: (id: string, body: AgreementDraftBody) =>
    authFetch<AgreementDetail>(`/agreements/${id}`, { method: 'PATCH', body }),
  propose: (id: string) => authFetch<AgreementDetail>(`/agreements/${id}/propose`, { method: 'POST' }),
  withdraw: (id: string) => authFetch<AgreementDetail>(`/agreements/${id}/withdraw`, { method: 'POST' }),
  accept: (id: string, teamIds: string[]) =>
    authFetch<AgreementDetail>(`/agreements/${id}/accept`, { method: 'POST', body: { team_ids: teamIds } }),
  reject: (id: string, reason: string | null) =>
    authFetch<AgreementDetail>(`/agreements/${id}/reject`, { method: 'POST', body: { reason } }),
  suspend: (id: string) => authFetch<AgreementDetail>(`/agreements/${id}/suspend`, { method: 'POST' }),
  resume: (id: string) => authFetch<AgreementDetail>(`/agreements/${id}/resume`, { method: 'POST' }),
  revoke: (id: string, reason: string | null) =>
    authFetch<AgreementDetail>(`/agreements/${id}/revoke`, { method: 'POST', body: { reason } }),
  activity: (id: string) => authFetch<AgreementActivity>(`/agreements/${id}/activity`),
  directory: () => authFetch<{ data: DirectoryCompany[] }>(`/companies/directory`),
};

/** Mirror of the PHP publisher's topic — the live feed of lifecycle events. */
export function agreementsTopic(companyId: string): string {
  return `/companies/${companyId}/agreements`;
}
